import React, { useEffect } from 'react';

import {
  FaArrowLeft,
  FaPhoneAlt,
  FaGlobe,
  FaStethoscope,
  FaHeartbeat,
  FaHospital,
  FaPills,
  FaUserMd,
  FaAmbulance,
  FaShieldAlt,
  FaAward,
  FaHandsHelping,
  FaCheckCircle,
  FaSyringe,
  FaXRay,
  FaBed,
  FaFlask,
  FaBullseye,
  FaEye,
  FaHeart,
  FaMicroscope,
  FaCut,
  FaAppleAlt,
  FaTooth,
  FaProcedures,
  FaClinicMedical,
  FaDna,
  FaArrowRight
} from 'react-icons/fa';

// ============================================================
// LOCAL IMAGE IMPORTS
// ============================================================

import ashrafiaLogo from '../logos/Ashrafiahospital/ashrafiahospitallogo.png';
import hospitalHero from '../logos/Ashrafiahospital/ashrafiahospital1.png';

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function AshrafHospital() {

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  // ==========================================================
  // BACK TO ALL SECTORS
  // ==========================================================

  const handleBackToSectors = () => {
    window.history.back();
  };

  return (
    <>
      <div className="hospital-page">

        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="hospital-hero">

          <div className="hero-background-glow glow-one"></div>
          <div className="hero-background-glow glow-two"></div>

          {/* =================================================
              BACK TO ALL SECTORS BUTTON
          ================================================= */}

          <button
            type="button"
            className="back-to-sectors"
            onClick={handleBackToSectors}
            aria-label="Back to All Sectors"
          >
            <span className="back-arrow">
              <FaArrowLeft />
            </span>

            <span className="back-text">
              Back to All Sectors
            </span>
          </button>

          <div className="hero-inner">

            {/* =================================================
                LEFT
            ================================================= */}

            <div className="hero-content reveal-left">

              <div className="ashrafia-brand">
                <img
                  src={ashrafiaLogo}
                  alt="Ashrafia - Ashraf Group of Industries"
                  className="ashrafia-logo"
                />
              </div>

              <div className="hospital-mini-brand">

                

                <div>

                  <div className="hospital-mini-title">
                    ASHRAF HOSPITAL
                  </div>

                  <div className="hospital-mini-tagline">
                    QUALITY CARE, COMPASSIONATE HEARTS
                  </div>

                </div>

              </div>

              <div className="hero-line"></div>

              <h1>
                CARE THAT
                <span> PUTS YOU FIRST</span>
              </h1>

              <p className="hero-description">
                Ashraf Hospital is committed to providing
                comprehensive, ethical and patient-centered
                healthcare services. With a team of experienced
                doctors and modern facilities, we ensure excellence
                in diagnosis, treatment and care.
              </p>

              <div className="hero-buttons">

                <a
                  href="#services"
                  className="hero-primary-button"
                >
                  Explore Services
                  <FaArrowRight />
                </a>

                <a
                  href="#appointments"
                  className="hero-secondary-button"
                >
                  <FaPhoneAlt />
                  Book Appointment
                </a>

              </div>

              <div className="hero-stats">

                <div className="hero-stat">
                  <strong>24/7</strong>
                  <span>Emergency Care</span>
                </div>

                <div className="stat-divider"></div>

                <div className="hero-stat">
                  <strong>10+</strong>
                  <span>Medical Services</span>
                </div>

                <div className="stat-divider"></div>

                <div className="hero-stat">
                  <strong>4+</strong>
                  <span>Specialists</span>
                </div>

              </div>

            </div>

            {/* =================================================
                RIGHT
            ================================================= */}

            <div className="hero-image-wrapper reveal-right">

              <div className="hero-image-decoration"></div>

              <div className="hero-image-container">

                <img
                  src={hospitalHero}
                  alt="Ashraf Hospital"
                  className="hero-hospital-image"
                />

                <div className="image-overlay"></div>

                <div className="emergency-badge">

                  <FaAmbulance />

                  <div>

                    <strong>
                      EMERGENCY
                    </strong>

                    <span>
                      24 / 7 AVAILABLE
                    </span>

                  </div>

                </div>

                <div className="hero-image-caption">

                  <span>
                    ASHRAF HOSPITAL
                  </span>

                  <small>
                    Quality Healthcare • Compassionate Service
                  </small>

                </div>

              </div>

            </div>

          </div>

        </section>

        {/* =====================================================
            SERVICES
        ===================================================== */}

        <section
          id="services"
          className="section services-section"
        >

          <div className="section-container">

            <div className="section-heading reveal-up">

              <div className="section-kicker">
                <FaStethoscope />
                OUR SERVICES
              </div>

              <h2>
                Comprehensive Healthcare
                <span> Under One Roof</span>
              </h2>

              <p>
                Professional healthcare services designed
                around your comfort, safety and wellbeing.
              </p>

            </div>

            <div className="services-grid">

              <ServiceCard
                icon={<FaStethoscope />}
                title="General OPD"
                text="Professional consultation and primary healthcare services."
              />

              <ServiceCard
                icon={<FaHeartbeat />}
                title="Cardiology"
                text="Specialized cardiac assessment, diagnosis and care."
              />

              <ServiceCard
                icon={<FaAmbulance />}
                title="Emergency Care"
                text="Round-the-clock emergency response and medical support."
              />

              <ServiceCard
                icon={<FaMicroscope />}
                title="Diagnostic Services"
                text="Reliable diagnostic procedures using modern facilities."
              />

              <ServiceCard
                icon={<FaCut />}
                title="Surgical Services"
                text="Professional surgical consultation and procedures."
              />

              <ServiceCard
                icon={<FaFlask />}
                title="Laboratory Services"
                text="Accurate laboratory testing and medical diagnostics."
              />

              <ServiceCard
                icon={<FaTooth />}
                title="ENT Services"
                text="Specialized care for ear, nose and throat conditions."
              />

              <ServiceCard
                icon={<FaHeartbeat />}
                title="Health Checkups"
                text="Preventive health assessments and wellness screening."
              />

              <ServiceCard
                icon={<FaAppleAlt />}
                title="Nutrition & Dietetics"
                text="Personalized nutritional guidance and diet planning."
              />

              <ServiceCard
                icon={<FaPills />}
                title="Pharmacy"
                text="Convenient pharmaceutical support for patients."
              />

            </div>

          </div>

        </section>

        {/* =====================================================
            FACILITIES
        ===================================================== */}

        <section className="section facilities-section">

          <div className="section-container">

            <div className="section-heading reveal-up">

              <div className="section-kicker">
                <FaHospital />
                OUR FACILITIES
              </div>

              <h2>
                Modern Facilities.
                <span> Better Care.</span>
              </h2>

              <p>
                A safe, modern and patient-focused healthcare
                environment built to support better outcomes.
              </p>

            </div>

            <div className="facilities-grid">

              <Facility
                icon={<FaProcedures />}
                title="Modern Operation Theatres"
              />

              <Facility
                icon={<FaBed />}
                title="Private Patient Rooms"
              />

              <Facility
                icon={<FaFlask />}
                title="Advanced Diagnostic Labs"
              />

              <Facility
                icon={<FaAmbulance />}
                title="24/7 Emergency Services"
              />

              <Facility
                icon={<FaXRay />}
                title="Digital X-Ray"
              />

              <Facility
                icon={<FaPills />}
                title="Pharmacy Services"
              />

              <Facility
                icon={<FaUserMd />}
                title="Experienced Medical Staff"
              />

              <Facility
                icon={<FaShieldAlt />}
                title="Hygienic & Safe Environment"
              />

            </div>

          </div>

        </section>

        {/* =====================================================
            DOCTORS
        ===================================================== */}

        <section className="section doctors-section">

          <div className="section-container">

            <div className="section-heading reveal-up">

              <div className="section-kicker">
                <FaUserMd />
                OUR DOCTORS
              </div>

              <h2>
                Experienced Medical
                <span> Professionals</span>
              </h2>

              <p>
                Dedicated healthcare professionals committed
                to delivering compassionate and quality care.
              </p>

            </div>

            <div className="doctors-grid">

              <Doctor
                name="Dr. Ammar Kaleem"
                specialization="Heart Specialist & General Physician"
                icon={<FaHeartbeat />}
                points={[
                  'Expert in Cardiac Care',
                  'ECG, ECHO & Stress Testing',
                  'Preventive & General Medicine'
                ]}
              />

              <Doctor
                name="Dr. Abrar Kalim"
                specialization="General Surgeon"
                icon={<FaCut />}
                points={[
                  'General & Laparoscopic Surgery',
                  'Trauma & Emergency Surgery',
                  'Surgical Consultations'
                ]}
              />

              <Doctor
                name="Dr. Usman Nazeer Meer"
                specialization="ENT Specialist"
                icon={<FaClinicMedical />}
                points={[
                  'Ear, Nose & Throat Disorders',
                  'Endoscopic ENT Surgeries',
                  'Hearing & Voice Care'
                ]}
              />

              <Doctor
                name="Dr. Nabeeha Khanam"
                specialization="Nutritionist"
                icon={<FaAppleAlt />}
                points={[
                  'Clinical Nutrition & Diet Planning',
                  'Weight Management',
                  'Lifestyle & Wellness Counseling'
                ]}
              />

            </div>

          </div>

        </section>

        {/* =====================================================
            MISSION / VISION / VALUES
        ===================================================== */}

        <section className="mvv-section">

          <div className="mvv-container">

            <MVV
              icon={<FaBullseye />}
              title="OUR MISSION"
              text="To deliver quality healthcare with compassion and dignity."
            />

            <MVV
              icon={<FaEye />}
              title="OUR VISION"
              text="To be a trusted healthcare leader in our community."
            />

            <div className="mvv-card">

              <div className="mvv-icon">
                <FaHeart />
              </div>

              <h3>
                OUR VALUES
              </h3>

              <div className="values-list">

                <span>
                  <FaCheckCircle />
                  Compassion
                </span>

                <span>
                  <FaCheckCircle />
                  Integrity
                </span>

                <span>
                  <FaCheckCircle />
                  Excellence
                </span>

                <span>
                  <FaCheckCircle />
                  Respect
                </span>

              </div>

            </div>

          </div>

        </section>

        {/* =====================================================
            APPOINTMENT / EMERGENCY
        ===================================================== */}

        <section
          id="appointments"
          className="appointment-section"
        >

          <div className="appointment-container">

            <div className="appointment-left">

              <div className="appointment-phone">
                <FaPhoneAlt />
              </div>

              <div>

                <span>
                  WE ARE HERE FOR YOU
                </span>

                <strong>
                  24/7
                </strong>

                <small>
                  EMERGENCY SERVICES
                </small>

              </div>

            </div>

            <div className="appointment-divider"></div>

            <div className="appointment-right">

              <span>
                FOR APPOINTMENTS
              </span>

              <a href="tel:03001234567">
                0300-1234567
              </a>

              <a href="tel:0511234567">
                051-1234567
              </a>

              <a
                href="https://www.ashrafhospital.com"
                target="_blank"
                rel="noreferrer"
              >
                <FaGlobe />
                www.ashrafhospital.com
              </a>

            </div>

          </div>

        </section>

        {/* =====================================================
            FOUR PILLARS
        ===================================================== */}

        <section className="pillars-section">

          <div className="pillars-container">

            <Pillar
              icon={<FaShieldAlt />}
              title="PATIENT SAFETY"
              text="Your safety is our top priority."
            />

            <Pillar
              icon={<FaUserMd />}
              title="EXPERIENCED TEAM"
              text="Qualified doctors & dedicated staff."
            />

            <Pillar
              icon={<FaAward />}
              title="QUALITY CARE"
              text="Evidence-based treatment with modern approach."
            />

            <Pillar
              icon={<FaHandsHelping />}
              title="COMPASSIONATE SERVICE"
              text="Care, empathy and respect for every patient."
            />

          </div>

        </section>

        {/* =====================================================
            FOOTER
        ===================================================== */}

        <footer className="hospital-footer">

          <div className="footer-inner">

            <div className="footer-number">
              13
            </div>

            <div className="footer-company">

              <strong>
                ASHRAF GROUP OF INDUSTRIES
              </strong>

              <span>
                Building Legacy. Powering the Future.
              </span>

            </div>

            <div className="footer-values">

              <FooterValue
                icon="🛡️"
                text="TRUST"
              />

              <FooterValue
                icon="🏅"
                text="QUALITY"
              />

              <FooterValue
                icon="💡"
                text="INNOVATION"
              />

              <FooterValue
                icon="👥"
                text="TEAMWORK"
              />

              <FooterValue
                icon="⭐"
                text="EXCELLENCE"
              />

            </div>

          </div>

        </footer>

      </div>

      {/* =====================================================
          COMPLETE CSS
      ===================================================== */}

      <style>{`

        * {
          box-sizing: border-box;
        }

        html {
          scroll-behavior: smooth;
        }

        body {
          margin: 0;
          padding: 0;
          font-family:
            Inter,
            Arial,
            Helvetica,
            sans-serif;
          background: #061323;
        }

        a {
          text-decoration: none;
        }

        button {
          font-family: inherit;
        }

        .hospital-page {
          width: 100%;
          min-height: 100vh;
          overflow-x: hidden;
          background: #ffffff;
          color: #10233f;
          animation: pageFadeIn .8s ease both;
        }

        /*
        ==========================================================
        FULL WIDTH
        ==========================================================
        */

        .hospital-hero,
        .section,
        .mvv-section,
        .appointment-section,
        .pillars-section,
        .hospital-footer {
          width: 100%;
          max-width: none;
        }

        .section-container,
        .mvv-container,
        .appointment-container,
        .pillars-container,
        .footer-inner {
          width: 100%;
          max-width: none;
        }

        /*
        ==========================================================
        BACK TO ALL SECTORS
        ==========================================================
        */

        .back-to-sectors {
          position: absolute;

          top: 25px;
          left: clamp(20px, 4vw, 65px);

          z-index: 20;

          display: inline-flex;
          align-items: center;
          gap: 10px;

          min-height: 44px;

          padding: 0 17px;

          border: 1px solid rgba(13, 39, 70, .16);

          border-radius: 7px;

          background: rgba(255, 255, 255, .88);

          color: #0d2746;

          font-size: 12px;
          font-weight: 800;

          letter-spacing: .2px;

          cursor: pointer;

          backdrop-filter: blur(12px);

          box-shadow:
            0 7px 22px rgba(13, 39, 70, .08);

          transition:
            transform .35s cubic-bezier(.2,.8,.2,1),
            background .35s ease,
            color .35s ease,
            border-color .35s ease,
            box-shadow .35s ease;
        }

        .back-to-sectors:hover {
          transform: translateX(5px) translateY(-2px);

          background: #0d2746;

          color: #ffffff;

          border-color: #0d2746;

          box-shadow:
            0 12px 28px rgba(13, 39, 70, .18);
        }

        .back-arrow {
          width: 27px;
          height: 27px;

          display: flex;
          align-items: center;
          justify-content: center;

          border-radius: 50%;

          background: #fff6df;

          color: #c38a25;

          transition:
            transform .35s ease,
            background .35s ease,
            color .35s ease;
        }

        .back-to-sectors:hover .back-arrow {
          transform: translateX(-3px);

          background: #d29a2d;

          color: #ffffff;
        }

        .back-text {
          white-space: nowrap;
        }

        /*
        ==========================================================
        GLOBAL ANIMATION
        ==========================================================
        */

        @keyframes pageFadeIn {
          from {
            opacity: 0;
          }

          to {
            opacity: 1;
          }
        }

        @keyframes revealLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }

          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes revealRight {
          from {
            opacity: 0;
            transform: translateX(60px) scale(.97);
          }

          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }

        @keyframes revealUp {
          from {
            opacity: 0;
            transform: translateY(35px);
          }

          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes emergencyPulse {
          0%,
          100% {
            box-shadow:
              0 10px 30px rgba(0,0,0,.25),
              0 0 0 0 rgba(198,40,40,.35);
          }

          50% {
            box-shadow:
              0 10px 30px rgba(0,0,0,.25),
              0 0 0 9px rgba(198,40,40,0);
          }
        }

        .reveal-left {
          animation:
            revealLeft .9s cubic-bezier(.2,.8,.2,1) both;
        }

        .reveal-right {
          animation:
            revealRight 1s cubic-bezier(.2,.8,.2,1) both;
        }

        .reveal-up {
          animation:
            revealUp .8s cubic-bezier(.2,.8,.2,1) both;
        }

        /*
        ==========================================================
        HERO
        ==========================================================
        */

        .hospital-hero {
          position: relative;
          width: 100%;
          min-height: 720px;
          overflow: hidden;

          background:
            linear-gradient(
              115deg,
              #ffffff 0%,
              #ffffff 47%,
              #f8f5ee 100%
            );
        }

        .hero-inner {
          width: 100%;
          max-width: none;
          margin: auto;

          min-height: 720px;

          padding:
            105px
            clamp(30px, 6vw, 100px)
            70px;

          display: grid;

          grid-template-columns:
            minmax(0, .9fr)
            minmax(0, 1.1fr);

          gap: 70px;
          align-items: center;

          position: relative;
          z-index: 2;
        }

        .hero-background-glow {
          position: absolute;
          width: 500px;
          height: 500px;
          border-radius: 50%;
          filter: blur(80px);
          opacity: .18;
          pointer-events: none;
        }

        .glow-one {
          background: #d79b28;
          left: -250px;
          top: 100px;
        }

        .glow-two {
          background: #12365e;
          right: -250px;
          bottom: -200px;
        }

        .ashrafia-brand {
          margin-bottom: 30px;
        }

        .ashrafia-logo {
          width: 190px;
          height: auto;
          object-fit: contain;
          display: block;

          transition:
            transform .4s ease,
            filter .4s ease;
        }

        .ashrafia-logo:hover {
          transform:
            translateY(-4px)
            scale(1.03);

          filter:
            drop-shadow(
              0 10px 18px rgba(10,45,80,.15)
            );
        }

        .hospital-mini-brand {
          display: flex;
          align-items: center;
          gap: 15px;
          margin-bottom: 18px;
        }

        .ah-logo {
          width: 68px;
          height: 68px;
          min-width: 68px;

          border-radius: 50%;

          display: flex;
          justify-content: center;
          align-items: center;

          background: #ffffff;
          border: 4px solid #b91c1c;

          box-shadow:
            0 7px 25px rgba(0,0,0,.12);

          position: relative;
        }

        .ah-logo::before {
          content: "♥";
          position: absolute;
          top: -21px;
          font-size: 27px;
          color: #b91c1c;
        }

        .ah-logo span {
          font-size: 23px;
          font-weight: 900;
          color: #101f36;
          letter-spacing: -2px;
        }

        .hospital-mini-title {
          color: #0e2543;

          font-family:
            Georgia,
            "Times New Roman",
            serif;

          font-size: 25px;
          font-weight: 900;
          letter-spacing: .5px;
        }

        .hospital-mini-tagline {
          margin-top: 5px;
          color: #d18d1d;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.4px;
        }

        .hero-line {
          width: 90px;
          height: 3px;
          margin: 18px 0;
          background: #d69a25;
          position: relative;
        }

        .hero-line::after {
          content: "";
          width: 8px;
          height: 8px;
          position: absolute;
          right: -4px;
          top: -2.5px;
          border-radius: 50%;
          background: #d69a25;
        }

        .hero-content h1 {
          margin: 0;

          color: #0b203b;

          font-family:
            Georgia,
            "Times New Roman",
            serif;

          font-size:
            clamp(48px, 5.2vw, 78px);

          line-height: .98;
          letter-spacing: -2px;
          font-weight: 900;
        }

        .hero-content h1 span {
          color: #c68b21;
          display: block;
          margin-top: 8px;
        }

        .hero-description {
          max-width: 610px;
          margin: 28px 0 0;
          color: #536176;
          font-size: 16px;
          line-height: 1.8;
        }

        /*
        ==========================================================
        HERO BUTTONS
        ==========================================================
        */

        .hero-buttons {
          display: flex;
          gap: 14px;
          flex-wrap: wrap;
          margin-top: 30px;
        }

        .hero-primary-button,
        .hero-secondary-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;

          min-height: 50px;
          padding: 0 22px;

          border-radius: 6px;

          font-weight: 800;
          font-size: 14px;

          transition:
            transform .3s ease,
            box-shadow .3s ease,
            background .3s ease;
        }

        .hero-primary-button {
          color: white;
          background: #0d2746;

          box-shadow:
            0 10px 25px rgba(13,39,70,.18);
        }

        .hero-primary-button:hover {
          transform: translateY(-4px);
          background: #c78b20;

          box-shadow:
            0 15px 30px rgba(199,139,32,.25);
        }

        .hero-secondary-button {
          color: #0d2746;
          border: 1px solid #d9b66e;
          background: #ffffff;
        }

        .hero-secondary-button:hover {
          transform: translateY(-4px);
          background: #fff8ea;

          box-shadow:
            0 10px 25px rgba(0,0,0,.08);
        }

        /*
        ==========================================================
        HERO STATS
        ==========================================================
        */

        .hero-stats {
          margin-top: 38px;

          display: flex;
          align-items: center;
          gap: 25px;
        }

        .hero-stat {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .hero-stat strong {
          font-size: 24px;
          color: #0d2746;
        }

        .hero-stat span {
          font-size: 11px;
          color: #758094;
          font-weight: 700;
        }

        .stat-divider {
          width: 1px;
          height: 40px;
          background: #d9dee6;
        }

        /*
        ==========================================================
        HERO IMAGE
        ==========================================================
        */

        .hero-image-wrapper {
          position: relative;
          width: 100%;
          min-height: 530px;

          display: flex;
          align-items: center;
          justify-content: center;
        }

        .hero-image-decoration {
          position: absolute;

          width: 90%;
          height: 95%;

          border: 2px solid #d7a33d;

          border-radius:
            55% 8px 8px 55%;

          right: -4%;
          top: 3%;

          transform: rotate(-1deg);
        }

        .hero-image-container {
          position: relative;

          width: 100%;
          height: 500px;

          overflow: hidden;

          border-radius:
            45% 10px 10px 45%;

          box-shadow:
            0 25px 60px rgba(8,30,52,.25);

          background: #0e2744;
        }

        .hero-hospital-image {
          width: 100%;
          height: 100%;

          object-fit: cover;
          object-position: center;

          display: block;

          transition:
            transform 1.2s cubic-bezier(.2,.8,.2,1),
            filter .6s ease;
        }

        .hero-image-container:hover
        .hero-hospital-image {
          transform: scale(1.07);
          filter: brightness(1.04);
        }

        .image-overlay {
          position: absolute;
          inset: 0;

          background:
            linear-gradient(
              180deg,
              rgba(5,25,45,.05) 30%,
              rgba(5,25,45,.72) 100%
            );
        }

        .emergency-badge {
          position: absolute;

          left: 25px;
          bottom: 25px;

          display: flex;
          align-items: center;

          gap: 12px;

          padding: 13px 18px;

          background: #c62828;
          color: white;

          border-radius: 7px;

          box-shadow:
            0 10px 30px rgba(0,0,0,.25);

          animation:
            emergencyPulse 2.2s infinite;
        }

        .emergency-badge > svg {
          font-size: 23px;
        }

        .emergency-badge div {
          display: flex;
          flex-direction: column;
        }

        .emergency-badge strong {
          font-size: 14px;
        }

        .emergency-badge span {
          margin-top: 2px;
          font-size: 9px;
          opacity: .85;
        }

        .hero-image-caption {
          position: absolute;

          right: 30px;
          bottom: 28px;

          display: flex;
          flex-direction: column;

          text-align: right;

          color: white;
        }

        .hero-image-caption span {
          font-size: 15px;
          font-weight: 900;
          letter-spacing: 1px;
        }

        .hero-image-caption small {
          margin-top: 5px;
          font-size: 9px;
          opacity: .85;
        }

        /*
        ==========================================================
        COMMON SECTIONS
        ==========================================================
        */

        .section {
          width: 100%;
          padding: 100px 25px;
        }

        .section-container {
          width: 100%;
          max-width: none;
          margin: auto;
        }

        .section-heading {
          max-width: 800px;
          margin: 0 auto 55px;
          text-align: center;
        }

        .section-kicker {
          display: inline-flex;
          align-items: center;
          gap: 8px;

          color: #c28b27;

          font-size: 12px;
          font-weight: 900;
          letter-spacing: 2px;

          margin-bottom: 13px;
        }

        .section-heading h2 {
          margin: 0;

          color: #0c2440;

          font-family:
            Georgia,
            "Times New Roman",
            serif;

          font-size:
            clamp(32px, 4vw, 52px);

          line-height: 1.1;
        }

        .section-heading h2 span {
          color: #c18a26;
        }

        .section-heading p {
          color: #68758a;
          line-height: 1.7;
          font-size: 15px;

          max-width: 650px;

          margin: 17px auto 0;
        }

        /*
        ==========================================================
        SERVICES
        ==========================================================
        */

        .services-section {
          background:
            linear-gradient(
              180deg,
              #ffffff 0%,
              #f7f9fc 100%
            );
        }

        .services-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 18px;
        }

        .service-card {
          min-height: 190px;

          padding: 28px 20px;

          border: 1px solid #e4e8ee;
          border-radius: 10px;

          background: white;

          position: relative;
          overflow: hidden;

          transition:
            transform .35s ease,
            box-shadow .35s ease,
            border-color .35s ease;
        }

        .service-card::before {
          content: "";

          position: absolute;

          width: 100%;
          height: 4px;

          left: 0;
          top: 0;

          background: #d39a2b;

          transform: scaleX(0);
          transform-origin: left;

          transition:
            transform .4s ease;
        }

        .service-card:hover {
          transform: translateY(-9px);

          border-color: #e0bd76;

          box-shadow:
            0 20px 40px rgba(20,45,70,.1);
        }

        .service-card:hover::before {
          transform: scaleX(1);
        }

        .service-icon {
          width: 54px;
          height: 54px;

          border-radius: 50%;

          display: flex;
          align-items: center;
          justify-content: center;

          color: #bd8420;
          background: #fff8e8;
          border: 1px solid #f0d89e;

          font-size: 21px;

          margin-bottom: 18px;

          transition:
            transform .4s ease,
            background .4s ease;
        }

        .service-card:hover .service-icon {
          transform: rotateY(180deg);
          background: #0d2948;
          color: #ffffff;
        }

        .service-card h3 {
          margin: 0 0 8px;
          color: #0d2746;
          font-size: 16px;
        }

        .service-card p {
          margin: 0;
          color: #718096;
          line-height: 1.55;
          font-size: 12px;
        }

        /*
        ==========================================================
        FACILITIES
        ==========================================================
        */

        .facilities-section {
          background: #ffffff;
        }

        .facilities-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
        }

        .facility-card {
          padding: 32px 20px;

          text-align: center;

          border: 1px solid #e5e9ef;

          background: #ffffff;

          border-radius: 10px;

          transition:
            transform .35s ease,
            background .35s ease,
            box-shadow .35s ease;
        }

        .facility-card:hover {
          transform: translateY(-8px);

          background: #fffaf0;

          box-shadow:
            0 15px 35px rgba(15,39,64,.09);
        }

        .facility-icon {
          width: 65px;
          height: 65px;

          margin: 0 auto 17px;

          border-radius: 50%;

          display: flex;
          align-items: center;
          justify-content: center;

          color: #c18a26;
          background: #fff8e8;
          border: 1px solid #efd69b;

          font-size: 25px;

          transition:
            transform .4s ease;
        }

        .facility-card:hover .facility-icon {
          transform:
            scale(1.12)
            rotate(5deg);
        }

        .facility-card h3 {
          margin: 0;
          color: #0d2746;
          font-size: 14px;
          line-height: 1.5;
        }

        /*
        ==========================================================
        DOCTORS
        ==========================================================
        */

        .doctors-section {
          background:
            linear-gradient(
              135deg,
              #071d35,
              #0c2c4c
            );
        }

        .doctors-section .section-kicker {
          color: #e2af4d;
        }

        .doctors-section .section-heading h2 {
          color: white;
        }

        .doctors-section .section-heading h2 span {
          color: #e3ae4b;
        }

        .doctors-section .section-heading p {
          color: #aebdcc;
        }

        .doctors-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
        }

        .doctor-card {
          position: relative;

          padding: 30px 23px;

          border-radius: 10px;

          background: #ffffff;

          overflow: hidden;

          transition:
            transform .4s ease,
            box-shadow .4s ease;
        }

        .doctor-card:hover {
          transform: translateY(-10px);

          box-shadow:
            0 25px 45px rgba(0,0,0,.25);
        }

        .doctor-top-icon {
          width: 62px;
          height: 62px;

          display: flex;
          justify-content: center;
          align-items: center;

          margin-bottom: 18px;

          color: #c38c28;
          background: #fff8e8;
          border: 1px solid #ecd49b;

          border-radius: 50%;

          font-size: 24px;

          transition:
            transform .4s ease;
        }

        .doctor-card:hover .doctor-top-icon {
          transform:
            scale(1.1)
            rotate(8deg);
        }

        .doctor-card h3 {
          margin: 0;
          color: #102a48;
          font-size: 18px;
        }

        .doctor-specialization {
          color: #c58d25;

          font-size: 12px;
          font-weight: 800;

          line-height: 1.5;

          margin-top: 7px;

          min-height: 38px;
        }

        .doctor-card ul {
          padding: 17px 0 0;
          margin: 15px 0 0;

          list-style: none;

          border-top:
            1px solid #e9edf2;
        }

        .doctor-card li {
          display: flex;
          gap: 8px;

          color: #637084;

          font-size: 11px;
          line-height: 1.5;

          margin-bottom: 9px;
        }

        .doctor-card li::before {
          content: "✓";
          color: #c58d25;
          font-weight: 900;
        }

        /*
        ==========================================================
        MISSION / VISION / VALUES
        ==========================================================
        */

        .mvv-section {
          width: 100%;
          padding: 80px 25px;
          background: #f7f9fb;
        }

        .mvv-container {
          width: 100%;
          max-width: none;

          margin: auto;

          display: grid;

          grid-template-columns:
            repeat(3, 1fr);

          gap: 20px;
        }

        .mvv-card {
          background: white;

          border: 1px solid #e3e8ee;
          border-radius: 10px;

          min-height: 220px;

          padding: 30px;

          text-align: center;

          transition:
            transform .35s ease,
            box-shadow .35s ease;
        }

        .mvv-card:hover {
          transform: translateY(-8px);

          box-shadow:
            0 20px 35px rgba(10,30,50,.1);
        }

        .mvv-icon {
          width: 55px;
          height: 55px;

          margin: 0 auto 15px;

          border-radius: 50%;

          display: flex;
          align-items: center;
          justify-content: center;

          background: #fff8e8;
          color: #c58d25;

          font-size: 21px;

          transition:
            transform .4s ease;
        }

        .mvv-card:hover .mvv-icon {
          transform:
            scale(1.12)
            rotate(8deg);
        }

        .mvv-card h3 {
          margin: 0 0 12px;

          color: #c18a26;

          font-size: 13px;
          letter-spacing: 1px;
        }

        .mvv-card p {
          color: #667488;

          font-size: 13px;
          line-height: 1.7;

          margin: 0;
        }

        .values-list {
          display: grid;

          grid-template-columns:
            1fr 1fr;

          gap: 9px;

          text-align: left;
        }

        .values-list span {
          display: flex;
          align-items: center;
          gap: 6px;

          color: #59677a;

          font-size: 12px;
        }

        .values-list svg {
          color: #c38b26;
          font-size: 11px;
        }

        /*
        ==========================================================
        APPOINTMENT
        ==========================================================
        */

        .appointment-section {
          width: 100%;

          padding: 65px 25px;

          background:
            linear-gradient(
              120deg,
              #071e37,
              #102e4e
            );
        }

        .appointment-container {
          width: 100%;
          max-width: none;

          margin: auto;

          padding: 30px 40px;

          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 35px;

          color: white;

          border:
            1px solid rgba(255,255,255,.12);

          border-radius: 12px;

          background:
            rgba(255,255,255,.04);

          box-shadow:
            0 20px 50px rgba(0,0,0,.15);

          transition:
            transform .4s ease,
            box-shadow .4s ease;
        }

        .appointment-container:hover {
          transform: translateY(-4px);

          box-shadow:
            0 25px 60px rgba(0,0,0,.22);
        }

        .appointment-left {
          display: flex;
          align-items: center;
          gap: 17px;
        }

        .appointment-phone {
          width: 65px;
          height: 65px;

          border-radius: 50%;

          background: #c62828;

          display: flex;
          align-items: center;
          justify-content: center;

          font-size: 24px;

          box-shadow:
            0 0 0 8px rgba(198,40,40,.12);

          animation:
            emergencyPulse 2.5s infinite;
        }

        .appointment-left div:last-child {
          display: flex;
          flex-direction: column;
        }

        .appointment-left span {
          color: #aebed0;
          font-size: 10px;
          letter-spacing: 1px;
        }

        .appointment-left strong {
          color: #e5ae48;
          font-size: 38px;
          line-height: 1;
          margin: 4px 0;
        }

        .appointment-left small {
          font-size: 10px;
          font-weight: 800;
        }

        .appointment-divider {
          width: 1px;
          height: 80px;
          background: rgba(255,255,255,.18);
        }

        .appointment-right {
          display: flex;
          flex-direction: column;

          align-items: flex-end;

          gap: 4px;
        }

        .appointment-right span {
          color: #e5ae48;

          font-size: 11px;
          font-weight: 900;

          letter-spacing: 1px;
        }

        .appointment-right a {
          color: white;

          font-size: 15px;
          font-weight: 800;

          transition:
            color .25s ease,
            transform .25s ease;
        }

        .appointment-right a:hover {
          color: #e4ae4a;
          transform: translateX(-4px);
        }

        .appointment-right a:last-child {
          margin-top: 5px;

          display: flex;
          align-items: center;

          gap: 6px;

          color: #b9c6d4;

          font-size: 10px;
        }

        /*
        ==========================================================
        FOUR PILLARS
        ==========================================================
        */

        .pillars-section {
          padding: 45px 25px;
          background: white;
        }

        .pillars-container {
          width: 100%;
          max-width: none;

          margin: auto;

          display: grid;

          grid-template-columns:
            repeat(4, 1fr);

          gap: 15px;
        }

        .pillar {
          display: flex;
          align-items: center;

          gap: 13px;

          padding: 18px;

          border:
            1px solid #e3e7ed;

          border-radius: 8px;

          background: #ffffff;

          transition:
            transform .3s ease,
            box-shadow .3s ease,
            background .3s ease;
        }

        .pillar:hover {
          transform: translateY(-5px);

          background: #fffaf0;

          box-shadow:
            0 15px 30px rgba(15,40,65,.08);
        }

        .pillar-icon {
          min-width: 46px;

          width: 46px;
          height: 46px;

          display: flex;

          align-items: center;
          justify-content: center;

          border-radius: 50%;

          background: #fff8e7;

          border:
            1px solid #eed69c;

          color: #c18b25;

          font-size: 18px;

          transition:
            transform .35s ease;
        }

        .pillar:hover .pillar-icon {
          transform:
            scale(1.12)
            rotate(7deg);
        }

        .pillar strong {
          display: block;

          color: #102943;

          font-size: 11px;

          margin-bottom: 4px;
        }

        .pillar span {
          display: block;

          color: #748094;

          font-size: 9px;

          line-height: 1.4;
        }

        /*
        ==========================================================
        FOOTER
        ==========================================================
        */

        .hospital-footer {
          width: 100%;

          background: #061b32;

          border-top:
            3px solid #c68d27;

          color: white;
        }

        .footer-inner {
          width: 100%;
          max-width: none;

          min-height: 110px;

          padding: 20px 35px;

          margin: auto;

          display: flex;

          align-items: center;

          gap: 25px;
        }

        .footer-number {
          color: white;

          background: #bd8522;

          padding: 8px 17px;

          font-size: 25px;

          font-weight: 900;

          border-radius: 3px;
        }

        .footer-company {
          display: flex;
          flex-direction: column;

          min-width: 250px;
        }

        .footer-company strong {
          font-size: 13px;
          letter-spacing: .5px;
        }

        .footer-company span {
          margin-top: 5px;

          color: #d6a23e;

          font-size: 11px;
        }

        .footer-values {
          margin-left: auto;

          display: flex;
          align-items: center;

          gap: 10px;
        }

        .footer-value {
          min-width: 90px;

          padding: 10px 8px;

          border-left:
            1px solid rgba(255,255,255,.15);

          text-align: center;

          transition:
            transform .3s ease,
            background .3s ease;
        }

        .footer-value:hover {
          transform: translateY(-5px);
          background: rgba(255,255,255,.05);
        }

        .footer-value-icon {
          display: block;

          font-size: 16px;

          margin-bottom: 3px;
        }

        .footer-value span {
          font-size: 8px;

          color: #d4dce6;

          font-weight: 800;
        }

        /*
        ==========================================================
        LARGE DESKTOP
        ==========================================================
        */

        @media (min-width: 1600px) {

          .hero-inner {
            padding-left: 7vw;
            padding-right: 7vw;
            gap: 6vw;
          }

          .back-to-sectors {
            left: 7vw;
          }

          .section-container,
          .mvv-container,
          .appointment-container,
          .pillars-container,
          .footer-inner {
            padding-left: 7vw !important;
            padding-right: 7vw !important;
          }

          .hero-content h1 {
            font-size: clamp(60px, 5vw, 92px);
          }

          .hero-image-container {
            height: 570px;
          }

          .hero-image-wrapper {
            min-height: 600px;
          }

          .service-card {
            min-height: 210px;
          }

          .section-heading h2 {
            font-size: 58px;
          }
        }

        /*
        ==========================================================
        TABLET
        ==========================================================
        */

        @media (max-width: 1150px) {

          .hero-inner {
            gap: 40px;

            padding-left: 45px;
            padding-right: 45px;
          }

          .services-grid {
            grid-template-columns:
              repeat(3, 1fr);
          }

          .doctors-grid {
            grid-template-columns:
              repeat(2, 1fr);
          }

          .footer-values {
            gap: 3px;
          }

        }

        /*
        ==========================================================
        TABLET SMALL
        ==========================================================
        */

        @media (max-width: 900px) {

          .back-to-sectors {
            top: 18px;
            left: 25px;
          }

          .hospital-hero {
            min-height: auto;
          }

          .hero-inner {
            min-height: auto;

            grid-template-columns: 1fr;

            padding-top: 100px;
            padding-bottom: 70px;
          }

          .hero-content {
            text-align: center;
          }

          .ashrafia-logo {
            margin: auto;
          }

          .hospital-mini-brand {
            justify-content: center;
            text-align: left;
          }

          .hero-line {
            margin-left: auto;
            margin-right: auto;
          }

          .hero-description {
            margin-left: auto;
            margin-right: auto;
          }

          .hero-buttons,
          .hero-stats {
            justify-content: center;
          }

          .hero-image-wrapper {
            min-height: 450px;
          }

          .hero-image-container {
            height: 450px;
            border-radius: 30px;
          }

          .hero-image-decoration {
            border-radius: 30px;
          }

          .facilities-grid {
            grid-template-columns:
              repeat(2, 1fr);
          }

          .pillars-container {
            grid-template-columns:
              repeat(2, 1fr);
          }

          .appointment-container {
            flex-wrap: wrap;
          }

          .appointment-divider {
            display: none;
          }

          .footer-inner {
            flex-wrap: wrap;
          }

          .footer-values {
            width: 100%;
            margin-left: 0;
            justify-content: center;
          }

        }

        /*
        ==========================================================
        MOBILE
        ==========================================================
        */

        @media (max-width: 600px) {

          .back-to-sectors {
            top: 15px;
            left: 17px;

            min-height: 40px;

            padding: 0 12px;

            gap: 7px;

            font-size: 10px;

            border-radius: 6px;
          }

          .back-arrow {
            width: 24px;
            height: 24px;

            font-size: 10px;
          }

          .hospital-hero {
            min-height: auto;
          }

          .hero-inner {
            padding:
              85px
              18px
              55px;

            gap: 45px;
          }

          .ashrafia-logo {
            width: 155px;
          }

          .hospital-mini-brand {
            gap: 11px;
          }

          .ah-logo {
            width: 57px;
            min-width: 57px;
            height: 57px;
          }

          .ah-logo span {
            font-size: 19px;
          }

          .hospital-mini-title {
            font-size: 19px;
          }

          .hospital-mini-tagline {
            font-size: 8px;
            letter-spacing: .8px;
          }

          .hero-content h1 {
            font-size:
              clamp(39px, 12vw, 58px);

            letter-spacing: -1.5px;
          }

          .hero-description {
            font-size: 13px;
            line-height: 1.7;
          }

          .hero-buttons {
            flex-direction: column;
          }

          .hero-primary-button,
          .hero-secondary-button {
            width: 100%;
          }

          .hero-stats {
            gap: 12px;
          }

          .hero-stat strong {
            font-size: 19px;
          }

          .hero-stat span {
            font-size: 8px;
          }

          .stat-divider {
            height: 32px;
          }

          .hero-image-wrapper {
            min-height: 330px;
          }

          .hero-image-container {
            height: 330px;
            border-radius: 22px;
          }

          .hero-image-decoration {
            display: none;
          }

          .emergency-badge {
            left: 12px;
            bottom: 12px;

            padding: 9px 11px;
          }

          .emergency-badge > svg {
            font-size: 18px;
          }

          .emergency-badge strong {
            font-size: 10px;
          }

          .emergency-badge span {
            font-size: 7px;
          }

          .hero-image-caption {
            display: none;
          }

          /*
          SECTIONS
          */

          .section {
            padding:
              65px
              17px;
          }

          .section-heading {
            margin-bottom: 35px;
          }

          .section-heading h2 {
            font-size: 32px;
          }

          .section-heading p {
            font-size: 13px;
          }

          /*
          SERVICES
          */

          .services-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .service-card {
            min-height: auto;
            padding: 22px;
          }

          /*
          FACILITIES
          */

          .facilities-grid {
            grid-template-columns:
              repeat(2, 1fr);

            gap: 10px;
          }

          .facility-card {
            min-height: 155px;
            padding: 20px 10px;
          }

          .facility-icon {
            width: 50px;
            height: 50px;
            font-size: 19px;
          }

          .facility-card h3 {
            font-size: 11px;
          }

          /*
          DOCTORS
          */

          .doctors-grid {
            grid-template-columns: 1fr;
          }

          .doctor-card {
            padding: 24px;
          }

          /*
          MVV
          */

          .mvv-section {
            padding:
              55px
              17px;
          }

          .mvv-container {
            grid-template-columns: 1fr;
          }

          .mvv-card {
            min-height: auto;
          }

          /*
          APPOINTMENT
          */

          .appointment-section {
            padding:
              40px
              17px;
          }

          .appointment-container {
            padding: 25px 20px;

            flex-direction: column;
            align-items: stretch;
          }

          .appointment-left {
            justify-content: center;
          }

          .appointment-right {
            align-items: center;
            text-align: center;
          }

          /*
          PILLARS
          */

          .pillars-section {
            padding:
              30px
              17px;
          }

          .pillars-container {
            grid-template-columns: 1fr;
          }

          .pillar {
            padding: 15px;
          }

          /*
          FOOTER
          */

          .footer-inner {
            padding:
              25px
              17px;

            flex-direction: column;

            text-align: center;
          }

          .footer-company {
            min-width: 0;
            align-items: center;
          }

          .footer-values {
            display: grid;

            grid-template-columns:
              repeat(2, 1fr);

            width: 100%;
          }

          .footer-value {
            min-width: 0;
          }

        }

        /*
        ==========================================================
        VERY SMALL MOBILE
        ==========================================================
        */

        @media (max-width: 380px) {

          .back-to-sectors {
            font-size: 9px;
            padding: 0 9px;
          }

          .hero-stats {
            gap: 7px;
          }

          .hero-stat strong {
            font-size: 17px;
          }

          .hero-stat span {
            font-size: 7px;
          }

          .facilities-grid {
            grid-template-columns: 1fr;
          }

          .footer-values {
            grid-template-columns:
              1fr 1fr;
          }

        }

        /*
        ==========================================================
        REDUCED MOTION
        ==========================================================
        */

        @media (prefers-reduced-motion: reduce) {

          *,
          *::before,
          *::after {
            animation-duration: .01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: .01ms !important;
            scroll-behavior: auto !important;
          }

        }

      `}</style>
    </>
  );
}

// ============================================================
// SERVICE CARD
// ============================================================

function ServiceCard({
  icon,
  title,
  text
}) {

  return (
    <div className="service-card">

      <div className="service-icon">
        {icon}
      </div>

      <h3>
        {title}
      </h3>

      <p>
        {text}
      </p>

    </div>
  );
}

// ============================================================
// FACILITY
// ============================================================

function Facility({
  icon,
  title
}) {

  return (
    <div className="facility-card">

      <div className="facility-icon">
        {icon}
      </div>

      <h3>
        {title}
      </h3>

    </div>
  );
}

// ============================================================
// DOCTOR
// ============================================================

function Doctor({
  name,
  specialization,
  icon,
  points
}) {

  return (
    <div className="doctor-card">

      <div className="doctor-top-icon">
        {icon}
      </div>

      <h3>
        {name}
      </h3>

      <div className="doctor-specialization">
        {specialization}
      </div>

      <ul>

        {points.map((point, index) => (

          <li key={index}>
            {point}
          </li>

        ))}

      </ul>

    </div>
  );
}

// ============================================================
// MISSION / VISION
// ============================================================

function MVV({
  icon,
  title,
  text
}) {

  return (
    <div className="mvv-card">

      <div className="mvv-icon">
        {icon}
      </div>

      <h3>
        {title}
      </h3>

      <p>
        {text}
      </p>

    </div>
  );
}

// ============================================================
// PILLAR
// ============================================================

function Pillar({
  icon,
  title,
  text
}) {

  return (
    <div className="pillar">

      <div className="pillar-icon">
        {icon}
      </div>

      <div>

        <strong>
          {title}
        </strong>

        <span>
          {text}
        </span>

      </div>

    </div>
  );
}

// ============================================================
// FOOTER VALUE
// ============================================================

function FooterValue({
  icon,
  text
}) {

  return (
    <div className="footer-value">

      <span className="footer-value-icon">
        {icon}
      </span>

      <span>
        {text}
      </span>

    </div>
  );
}